import { SalesTransaction } from '../types/transaction';
import { RepairWithId, RepairStatus } from '../types/repair';
import { ReportData, ReportSoldItem, SoldItemsGroup } from '../types/reports';
import { getTransactionsByDateRange } from './transactionService';
import { getPickedUpRepairsByDateRange } from './repairService';
import { getAllCategories } from './categoryService';
import { convertUsdToTry, getCurrentExchangeRate } from './exchangeRateService';
import { Currency, ItemType } from '../types/item';
import { withAuthentication } from './authService';
import { Timestamp } from 'firebase/firestore';

/**
 * Groups sold items by category or type
 */
const groupSoldItems = (items: ReportSoldItem[], groupBy: 'category' | 'type', currentRate: number): SoldItemsGroup[] => {
  const groups = new Map<string, ReportSoldItem[]>();
  
  items.forEach(item => {
    let groupKey: string;
    let groupName: string;
    
    if (groupBy === 'category') {
      groupKey = item.categoryId || 'unknown';
      groupName = item.categoryName || 'Bilinmeyen Kategori';
    } else {
      groupKey = item.itemType || 'unknown';
      groupName = getItemTypeDisplayName(item.itemType);
    }
    
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(item);
  });
  
  return Array.from(groups.entries()).map(([groupKey, groupItems]) => {
    const totalRevenue = groupItems.reduce((sum, item) => {
      // Convert to TRY if needed for consistent totals
      if (item.sellingCurrency === Currency.USD) {
        // Use the exchange rate from the transaction or fallback to current rate
        return sum + convertUsdToTry(item.sellingPrice, currentRate);
      }
      return sum + item.sellingPrice;
    }, 0);
    
    const groupName = groupBy === 'category' 
      ? groupItems[0]?.categoryName || 'Bilinmeyen Kategori'
      : getItemTypeDisplayName(groupItems[0]?.itemType);
    
    return {
      groupKey,
      groupName,
      items: groupItems.sort((a, b) => b.saleDate.getTime() - a.saleDate.getTime()),
      totalCount: groupItems.length,
      totalRevenue,
      groupType: groupBy
    };
  }).sort((a, b) => b.totalRevenue - a.totalRevenue); // Sort by revenue descending
};

/**
 * Get display name for item type
 */
const getItemTypeDisplayName = (itemType?: ItemType): string => {
  switch (itemType) {
    case ItemType.SILVER:
      return 'Gümüş Ürünler';
    case ItemType.WATCH:
      return 'Saat';
    case ItemType.SERVICE:
      return 'Hizmet';
    default:
      return 'Bilinmeyen Tür';
  }
};

const _generateReport = async (startDate: Date, endDate: Date): Promise<ReportData> => {
  try {
    const [transactions, repairs, categories] = await Promise.all([
      getTransactionsByDateRange(startDate, endDate),
      getPickedUpRepairsByDateRange(startDate, endDate),
      getAllCategories()
    ]);

    // Create a map for quick category lookup
    const categoryMap = new Map(categories.map(cat => [cat.id, cat.name]));

    let totalItemSalesInTry = 0;
    let totalCostOfGoodsSoldInTry = 0;
    const soldItems: ReportSoldItem[] = [];

    const currentRate = await getCurrentExchangeRate(); // Fallback rate

    for (const transaction of transactions) {
      let salePriceInTry: number;
      if (transaction.sellingCurrency === Currency.TRY) {
        salePriceInTry = transaction.sellingPrice;
      } else {
        salePriceInTry = convertUsdToTry(transaction.sellingPrice, transaction.sellingExchangeRateToTry || currentRate);
      }
      totalItemSalesInTry += salePriceInTry;

      let costPriceInTry: number;
      if (transaction.costCurrency === Currency.TRY) {
        costPriceInTry = transaction.costPrice;
      } else {
        costPriceInTry = convertUsdToTry(transaction.costPrice, transaction.costExchangeRateToTry || currentRate);
      }
      totalCostOfGoodsSoldInTry += costPriceInTry;

      soldItems.push({
        id: transaction.itemId,
        name: transaction.itemName,
        sellingPrice: transaction.sellingPrice,
        sellingCurrency: transaction.sellingCurrency,
        saleDate: transaction.saleDate instanceof Timestamp ? transaction.saleDate.toDate() : new Date(transaction.saleDate),
        // Enhanced fields for grouping
        categoryId: transaction.itemCategory,
        categoryName: transaction.itemCategory ? categoryMap.get(transaction.itemCategory) : undefined,
        itemType: transaction.itemType,
      });
    }

    let totalRepairIncomeInTry = 0;
    for (const repair of repairs) {
      if (repair.finalPrice) {
        // Assuming repair finalPrice is always in TRY. If not, conversion needed here.
        totalRepairIncomeInTry += repair.finalPrice;
      }
    }

    const totalSalesInTry = totalItemSalesInTry + totalRepairIncomeInTry;
    const totalProfitInTry = totalSalesInTry - totalCostOfGoodsSoldInTry;

    // Generate grouped data
    const soldItemsGrouped = {
      byCategory: groupSoldItems(soldItems, 'category', currentRate),
      byType: groupSoldItems(soldItems, 'type', currentRate),
    };

    return {
      totalSalesInTry,
      totalCostOfGoodsSoldInTry,
      totalProfitInTry,
      totalRepairIncomeInTry,
      soldItems,
      soldItemsGrouped,
      dateRange: { start: startDate, end: endDate },
    };
  } catch (error) {
    console.error('Error generating report:', error);
    throw new Error('Rapor oluşturulurken bir hata oluştu.');
  }
};

export const generateReport = withAuthentication(_generateReport);